import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useMsal } from "@azure/msal-react";
import { useAuth } from "../AuthProvider";
import { getApiToken } from "../authUtils";
import urlconfig from "../config/urlconfig";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Button,
  Skeleton,
  Alert,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Snackbar,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import StorefrontIcon from "@mui/icons-material/Storefront";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PublishOutlinedIcon from "@mui/icons-material/PublishOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";

import {
  catalogTokens,
  colorForTag,
  formatPriceRange,
  parseApiError,
  parseServerTimestamp,
  buildVendorCatalogFilterParams,
  formatUploaderName,
} from "./VendorCatalogTheme";
import { formatDistanceToNow } from "date-fns";
import VendorCatalogEditDialog from "./VendorCatalogEditDialog";
import VendorCatalogReplaceDialog from "./VendorCatalogReplaceDialog";
import VendorCatalogReplacePriceListDialog from "./VendorCatalogReplacePriceListDialog";

// One vendor's catalogs are loaded when the dialog opens, and reloaded whenever the user flips the
// "show all" toggle (if filters are active) or after a successful edit/replace/delete. The table
// shows a loading state while the fetch is in progress, and an error state if it fails. The
// "View" buttons open the catalog/price list in a new tab via a backend-generated signed URL.
const VendorCatalogVendorDialog = ({
  vendor,
  open,
  onClose,
  canManage,
  onChanged,
  filters,
  selectedMonth,
}) => {
  const { instance } = useMsal();
  const { account } = useAuth();

  const [catalogs, setCatalogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openingId, setOpeningId] = useState(null);
  const [vendorContact, setVendorContact] = useState(null);

  // This state is reset to false whenever the vendor changes,
  // so a new vendor always starts with filtered view.
  const [showAllCatalogs, setShowAllCatalogs] = useState(false);

  const filterParams = useMemo(
    () => buildVendorCatalogFilterParams(filters, selectedMonth),
    [filters, selectedMonth]
  );

  const hasActiveFilters = Object.keys(filterParams).length > 0;

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuCatalog, setMenuCatalog] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [replaceTarget, setReplaceTarget] = useState(null);
  const [replacePriceListTarget, setReplacePriceListTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [regeneratingId, setRegeneratingId] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const authedFetch = useCallback(
    async (url, method = "GET") => {
      const token = await getApiToken(instance, account);

      const res = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        throw new Error(await parseApiError(res));
      }

      const text = await res.text();

      return text ? JSON.parse(text) : {};
    },
    [instance, account]
  );

  const loadCatalogs = useCallback(
    (silent = false) => {
      if (!vendor?.id || !account) return;

      if (!silent) {
        setLoading(true);
      }

      setError(null);

      const params = new URLSearchParams({
        vendorId: vendor.id,
        size: 100,
        ...(showAllCatalogs ? {} : filterParams),
      });

      return authedFetch(
        `${urlconfig.backend_api_base_url}/api/v2/vendor-catalogs?${params.toString()}`
      )
        .then((data) => {
          setCatalogs(data.content || []);
        })
        .catch((err) => {
          setError(
            err.message || "Could not load this vendor's catalogs"
          );
        })
        .finally(() => {
          if (!silent) {
            setLoading(false);
          }
        });
    },
    [
      vendor?.id,
      account,
      authedFetch,
      filterParams,
      showAllCatalogs,
    ]
  );

  // Reset "show all" toggle whenever the vendor changes,
  // so a new vendor always starts with filtered view.
  useEffect(() => {
    setShowAllCatalogs(false);
  }, [vendor?.id]);

  useEffect(() => {
    if (!open) return;

    loadCatalogs();
  }, [open, vendor?.id, account]);

  // Contact name/phone (vendorSpoc/vendorSpocNumber) live on VendorRegistration,
  // not on the catalog DTOs the search grid groups into `vendor` ({ id, name }) —
  // fetched separately here.
  useEffect(() => {
    if (!open || !vendor?.id || !account) return;

    setVendorContact(null);

    authedFetch(
      `${urlconfig.backend_api_base_url}/api/v2/vendors/${vendor.id}`
    )
      .then((data) => {
        setVendorContact({
          vendorSpoc: data?.vendorSpoc,
          vendorSpocNumber: data?.vendorSpocNumber,
        });
      })
      .catch(() => {
        setVendorContact(null);
      });
  }, [open, vendor?.id, account, authedFetch]);

  // Re-fetch silently when the user flips the "show all" toggle
  // while the dialog is open.
  useEffect(() => {
    if (!open) return;

    loadCatalogs(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAllCatalogs]);

  const refresh = () => {
    loadCatalogs(true);
    onChanged?.();
  };

  const openUrl = async (catalogId, urlField) => {
    setOpeningId(`${catalogId}-${urlField}`);

    try {
      const data = await authedFetch(
        `${urlconfig.backend_api_base_url}/api/v2/vendor-catalogs/${catalogId}/${urlField}`
      );

      window.open(data.url, "_blank", "noopener,noreferrer");
    } catch (err) {
      setError(err.message || "Could not open the PDF");
    } finally {
      setOpeningId(null);
    }
  };

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuCatalog(null);
  };

  const handleEditSuccess = () => {
    setEditTarget(null);

    setSnackbar({
      open: true,
      message: "Catalog updated.",
      severity: "success",
    });

    refresh();
  };

  const handleReplaceSuccess = () => {
    setReplaceTarget(null);

    setSnackbar({
      open: true,
      message: "Catalog replaced.",
      severity: "success",
    });

    refresh();
  };

  const handleReplacePriceListSuccess = () => {
    const wasAdding = !replacePriceListTarget?.hasSeparatePriceList;
    setReplacePriceListTarget(null);

    setSnackbar({
      open: true,
      message: wasAdding ? "Price list added." : "Price list replaced.",
      severity: "success",
    });

    refresh();
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    const id = deleteTarget.id;

    setDeletingId(id);

    try {
      await authedFetch(
        `${urlconfig.backend_api_base_url}/api/v2/vendor-catalogs/${id}`,
        "DELETE"
      );

      setDeleteTarget(null);

      setSnackbar({
        open: true,
        message: "Catalog deleted.",
        severity: "success",
      });

      refresh();
    } catch (err) {
      setError(err.message || "Delete failed");
    } finally {
      setDeletingId(null);
    }
  };

  const handleRegenerateSummary = async (catalog) => {
    setRegeneratingId(catalog.id);

    try {
      await authedFetch(
        `${urlconfig.backend_api_base_url}/api/v2/vendor-catalogs/${catalog.id}/regenerate-summary`,
        "POST"
      );

      setSnackbar({
        open: true,
        message:
          "Regenerating summary — this can take a few seconds.",
        severity: "success",
      });

      setTimeout(() => {
        loadCatalogs(true);
        setRegeneratingId(null);
      }, 6000);
    } catch (err) {
      setSnackbar({
        open: true,
        message:
          err.message || "Failed to start regeneration",
        severity: "error",
      });

      setRegeneratingId(null);
    }
  };

  return (
    <>
      {/* =========================================================
          90% SCREEN VENDOR CATALOG DIALOG
          ========================================================= */}
      <Dialog
        open={open}
        onClose={onClose}
        fullScreen
        PaperProps={{
          sx: {
            width: "90%",
            height: "90%",
            borderRadius: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
        }}
      >
        {/* =======================================================
            HEADER
            ======================================================= */}
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            px: { xs: 2, sm: 3 },
            py: 1.75,
            borderBottom: `1px solid ${catalogTokens.color.border}`,
            backgroundColor: "#ffffff",
            flexShrink: 0,
          }}
        >
          {/* Vendor Information */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              minWidth: 0,
              flex: 1,
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                flexShrink: 0,
                borderRadius: `${catalogTokens.radius.md}px`,
                bgcolor: catalogTokens.color.primarySoft,
                color: catalogTokens.color.primary,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <StorefrontIcon fontSize="small" />
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="subtitle1"
                fontWeight={800}
                noWrap
                sx={{
                  lineHeight: 1.3,
                }}
              >
                {vendor?.name}
              </Typography>

              <Typography
                variant="caption"
                sx={{
                  color: catalogTokens.color.textSecondary,
                  display: "block",
                  mt: 0.25,
                }}
                noWrap
              >
                {[
                  vendorContact?.vendorSpoc,
                  vendorContact?.vendorSpocNumber,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </Typography>
            </Box>
          </Box>

          {/* Catalog Count */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                px: 1.25,
                py: 0.6,
                borderRadius: `${catalogTokens.radius.md}px`,
                backgroundColor: catalogTokens.color.primarySoft,
                color: catalogTokens.color.primary,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 800,
                  whiteSpace: "nowrap",
                }}
              >
                {loading
                  ? "Loading catalogs…"
                  : `${catalogs.length} ${
                      catalogs.length === 1
                        ? "catalog"
                        : "catalogs"
                    }`}
              </Typography>
            </Box>
          </Box>

          {/* Close */}
          <IconButton
            size="small"
            onClick={onClose}
            sx={{
              color: catalogTokens.color.textSecondary,
              flexShrink: 0,
              "&:hover": {
                backgroundColor: "#f3f4f6",
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        {/* =======================================================
            CONTENT
            ======================================================= */}
        <DialogContent
          sx={{
            flex: 1,
            minHeight: 0,
            p: { xs: 1.5, sm: 2.5 },
            backgroundColor: "#fafbfc",
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          {/* =====================================================
              FILTER STATUS
              ===================================================== */}
          {hasActiveFilters && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                mb: 2,
                px: 1.75,
                py: 1.1,
                borderRadius: `${catalogTokens.radius.md}px`,
                bgcolor: catalogTokens.color.primarySoft,
                border: `1px solid ${catalogTokens.color.border}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: catalogTokens.color.primary,
                  fontWeight: 700,
                }}
              >
                {showAllCatalogs
                  ? "Showing every catalog for this vendor"
                  : "Showing only catalogs matching your search filters"}
              </Typography>

              <Button
                size="small"
                onClick={() =>
                  setShowAllCatalogs((prev) => !prev)
                }
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  color: catalogTokens.color.primary,
                  whiteSpace: "nowrap",
                }}
              >
                {showAllCatalogs
                  ? "Show filtered only"
                  : "Show all catalogs"}
              </Button>
            </Box>
          )}

          {/* =====================================================
              ERROR
              ===================================================== */}
          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                borderRadius: `${catalogTokens.radius.md}px`,
              }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {/* =====================================================
              LOADING
              ===================================================== */}
          {loading ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton
                  key={i}
                  variant="rounded"
                  height={72}
                  sx={{
                    borderRadius: `${catalogTokens.radius.md}px`,
                  }}
                />
              ))}
            </Box>
          ) : catalogs.length === 0 ? (
            /* ===================================================
               EMPTY STATE
               =================================================== */
            <Box
              sx={{
                py: 8,
                px: 2,
                textAlign: "center",
                backgroundColor: "#ffffff",
                border: `1px solid ${catalogTokens.color.border}`,
                borderRadius: `${catalogTokens.radius.md}px`,
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  mx: "auto",
                  mb: 1.5,
                  borderRadius: "50%",
                  backgroundColor:
                    catalogTokens.color.primarySoft,
                  color: catalogTokens.color.primary,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <StorefrontIcon />
              </Box>

              <Typography
                variant="body2"
                sx={{
                  color: catalogTokens.color.textSecondary,
                }}
              >
                {hasActiveFilters && !showAllCatalogs
                  ? "No catalogs for this vendor match your search filters."
                  : "No catalogs found for this vendor."}
              </Typography>

              {hasActiveFilters && !showAllCatalogs && (
                <Button
                  size="small"
                  onClick={() => setShowAllCatalogs(true)}
                  sx={{
                    mt: 1,
                    textTransform: "none",
                    fontWeight: 700,
                    color: catalogTokens.color.primary,
                  }}
                >
                  Show all catalogs for this vendor
                </Button>
              )}
            </Box>
          ) : (
            /* ===================================================
               CATALOG TABLE
               =================================================== */
            <Box
              sx={{
                width: "100%",
                overflowX: "hidden",
                backgroundColor: "#ffffff",
                border: `1px solid ${catalogTokens.color.border}`,
                borderRadius: `${catalogTokens.radius.md}px`,
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
              }}
            >
              <Table
                size="small"
                sx={{
                  width: "100%",
                  tableLayout: "fixed",
                  borderCollapse: "separate",
                  borderSpacing: 0,
                }}
              >
                {/* =================================================
                    TABLE HEADER
                    ================================================= */}
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: "#f8fafc",
                    }}
                  >
                    <TableCell
                      sx={{
                        width: "16%",
                        fontWeight: 800,
                        color: catalogTokens.color.textPrimary,
                        fontSize: 13,
                        borderBottom: `1px solid ${catalogTokens.color.border}`,
                        py: 1.5,
                        px: 1.5,
                      }}
                    >
                      Brands
                    </TableCell>

                    <TableCell
                      sx={{
                        width: "17%",
                        fontWeight: 800,
                        color: catalogTokens.color.textPrimary,
                        fontSize: 13,
                        borderBottom: `1px solid ${catalogTokens.color.border}`,
                        py: 1.5,
                        px: 1.5,
                      }}
                    >
                      Categories
                    </TableCell>

                    <TableCell
                      sx={{
                        width: "25%",
                        fontWeight: 800,
                        color: catalogTokens.color.textPrimary,
                        fontSize: 13,
                        borderBottom: `1px solid ${catalogTokens.color.border}`,
                        py: 1.5,
                        px: 1.5,
                      }}
                    >
                      Sub-Categories
                    </TableCell>

                    <TableCell
                      sx={{
                        width: "12%",
                        fontWeight: 800,
                        color: catalogTokens.color.textPrimary,
                        fontSize: 13,
                        borderBottom: `1px solid ${catalogTokens.color.border}`,
                        py: 1.5,
                        px: 1.5,
                      }}
                    >
                      Price Range
                    </TableCell>

                    <TableCell
                      sx={{
                        width: "14%",
                        fontWeight: 800,
                        color: catalogTokens.color.textPrimary,
                        fontSize: 13,
                        borderBottom: `1px solid ${catalogTokens.color.border}`,
                        py: 1.5,
                        px: 1.5,
                      }}
                    >
                      Uploaded By
                    </TableCell>

                    <TableCell
                      align="right"
                      sx={{
                        width: "16%",
                        fontWeight: 800,
                        color: catalogTokens.color.textPrimary,
                        fontSize: 13,
                        borderBottom: `1px solid ${catalogTokens.color.border}`,
                        py: 1.5,
                        px: 1.5,
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>

                {/* =================================================
                    TABLE BODY
                    ================================================= */}
                <TableBody>
                  {catalogs.map((catalog) => {
                    const uploaded = parseServerTimestamp(
                      catalog.uploadedAt
                    );

                    const hasSummary = Boolean(
                      catalog.catalogSummary &&
                        catalog.catalogSummary.trim()
                    );

                    return (
                      <TableRow
                        key={catalog.id}
                        hover
                        sx={{
                          "&:last-child td": {
                            borderBottom: 0,
                          },
                          "&:hover": {
                            backgroundColor: "#fafafa",
                          },
                        }}
                      >
                        {/* =========================================
                            BRANDS
                            ========================================= */}
                        <TableCell
                          sx={{
                            verticalAlign: "top",
                            py: 1.5,
                            px: 1.5,
                            borderBottom: `1px solid ${catalogTokens.color.border}`,
                            wordBreak: "break-word",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              alignItems: "flex-start",
                              gap: 0.6,
                              width: "100%",
                            }}
                          >
                            {(catalog.brands || []).map((b) => {
                              const color = colorForTag(b.name);

                              return (
                                <Chip
                                  key={b.id ?? b.name}
                                  size="small"
                                  label={b.name}
                                  sx={{
                                    bgcolor: color.bg,
                                    color: color.fg,
                                    fontWeight: 700,
                                    maxWidth: "100%",
                                    height: 28,
                                    "& .MuiChip-label": {
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "normal",
                                      px: 1,
                                    },
                                  }}
                                />
                              );
                            })}

                            {(catalog.brands || []).length === 0 && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color:
                                    catalogTokens.color
                                      .textSecondary,
                                }}
                              >
                                —
                              </Typography>
                            )}
                          </Box>
                        </TableCell>

                        {/* =========================================
                            CATEGORIES
                            ========================================= */}
                        <TableCell
                          sx={{
                            verticalAlign: "top",
                            py: 1.5,
                            px: 1.5,
                            borderBottom: `1px solid ${catalogTokens.color.border}`,
                            wordBreak: "break-word",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              alignItems: "flex-start",
                              gap: 0.6,
                              width: "100%",
                            }}
                          >
                            {(catalog.categories || []).map(
                              (c) => {
                                const color = colorForTag(
                                  c.name
                                );

                                return (
                                  <Chip
                                    key={c.id ?? c.name}
                                    size="small"
                                    label={c.name}
                                    sx={{
                                      bgcolor: color.bg,
                                      color: color.fg,
                                      fontWeight: 600,
                                      maxWidth: "100%",
                                      height: 28,
                                      "& .MuiChip-label": {
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "normal",
                                        px: 1,
                                      },
                                    }}
                                  />
                                );
                              }
                            )}

                            {(catalog.categories || []).length ===
                              0 && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color:
                                    catalogTokens.color
                                      .textSecondary,
                                }}
                              >
                                —
                              </Typography>
                            )}
                          </Box>
                        </TableCell>

                        {/* =========================================
                            SUB-CATEGORIES
                            ========================================= */}
                        <TableCell
                          sx={{
                            verticalAlign: "top",
                            py: 1.5,
                            px: 1.5,
                            borderBottom: `1px solid ${catalogTokens.color.border}`,
                            wordBreak: "break-word",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              alignItems: "flex-start",
                              gap: 0.6,
                              width: "100%",
                            }}
                          >
                            {(catalog.subCategories || []).map(
                              (sc) => (
                                <Chip
                                  key={
                                    sc.id ??
                                    `${sc.categoryName}::${sc.name}`
                                  }
                                  size="small"
                                  variant="outlined"
                                  label={sc.name}
                                  sx={{
                                    borderColor:
                                      catalogTokens.color.border,
                                    color:
                                      catalogTokens.color
                                        .textSecondary,
                                    fontWeight: 600,
                                    maxWidth: "100%",
                                    height: 28,
                                    backgroundColor: "#ffffff",
                                    "& .MuiChip-label": {
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "normal",
                                      px: 1,
                                    },
                                  }}
                                />
                              )
                            )}

                            {(catalog.subCategories || [])
                              .length === 0 && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color:
                                    catalogTokens.color
                                      .textSecondary,
                                }}
                              >
                                —
                              </Typography>
                            )}
                          </Box>
                        </TableCell>

                        {/* =========================================
                            PRICE RANGE
                            ========================================= */}
                        <TableCell
                          sx={{
                            verticalAlign: "top",
                            py: 1.5,
                            px: 1.5,
                            borderBottom: `1px solid ${catalogTokens.color.border}`,
                            wordBreak: "break-word",
                          }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            sx={{
                              color:
                                catalogTokens.color.textPrimary,
                              lineHeight: 1.5,
                            }}
                          >
                            {formatPriceRange(
                              catalog.minPrice,
                              catalog.maxPrice
                            )}
                          </Typography>
                        </TableCell>

                        {/* =========================================
                            UPLOADED BY
                            ========================================= */}
                        <TableCell
                          sx={{
                            verticalAlign: "top",
                            py: 1.5,
                            px: 1.5,
                            borderBottom: `1px solid ${catalogTokens.color.border}`,
                            wordBreak: "break-word",
                          }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{
                              overflowWrap: "anywhere",
                              lineHeight: 1.4,
                            }}
                            title={catalog.uploadedBy}
                          >
                            {formatUploaderName(catalog.uploadedBy)}
                          </Typography>

                          <Typography
                            variant="caption"
                            sx={{
                              color:
                                catalogTokens.color
                                  .textSecondary,
                              display: "block",
                              mt: 0.4,
                            }}
                          >
                            {uploaded
                              ? formatDistanceToNow(uploaded, {
                                  addSuffix: true,
                                })
                              : "—"}
                          </Typography>
                        </TableCell>

                        {/* =========================================
                            ACTIONS
                            ========================================= */}
                        <TableCell
                          align="right"
                          sx={{
                            verticalAlign: "top",
                            py: 1.5,
                            px: 1.5,
                            borderBottom: `1px solid ${catalogTokens.color.border}`,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "flex-end",
                              alignItems: "center",
                              flexWrap: "wrap",
                              gap: 0.5,
                            }}
                          >
                            {/* AI Summary */}
                            <Tooltip
                              title={
                                hasSummary
                                  ? catalog.catalogSummary
                                  : regeneratingId ===
                                    catalog.id
                                  ? "Regenerating…"
                                  : "AI summary not available for this catalog yet"
                              }
                              arrow
                              slotProps={{
                                tooltip: {
                                  sx: {
                                    maxWidth: 280,
                                    fontSize: 12.5,
                                    lineHeight: 1.5,
                                    p: 1.25,
                                  },
                                },
                              }}
                            >
                              <IconButton
                                size="small"
                                sx={{
                                  color: hasSummary
                                    ? catalogTokens.color
                                        .primary
                                    : catalogTokens.color
                                        .textSecondary,
                                  opacity: hasSummary ? 1 : 0.5,
                                }}
                              >
                                {regeneratingId ===
                                catalog.id ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <AutoFixHighIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>

                            {/* View Catalog */}
                            <Button
                              size="small"
                              variant="contained"
                              disableElevation
                              startIcon={
                                <PictureAsPdfIcon fontSize="small" />
                              }
                              onClick={() =>
                                openUrl(
                                  catalog.id,
                                  "download-url"
                                )
                              }
                              disabled={
                                openingId ===
                                `${catalog.id}-download-url`
                              }
                              sx={{
                                textTransform: "none",
                                fontWeight: 700,
                                minWidth: 72,
                                borderRadius: `${catalogTokens.radius.md}px`,
                                bgcolor:
                                  catalogTokens.color.primary,
                                "&:hover": {
                                  bgcolor:
                                    catalogTokens.color
                                      .primaryDark,
                                },
                              }}
                            >
                              View
                            </Button>

                            {/* Separate Price List */}
                            {catalog.hasSeparatePriceList && (
                              <Tooltip
                                title="View separate price list PDF"
                                arrow
                              >
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      openUrl(
                                        catalog.id,
                                        "price-list-download-url"
                                      )
                                    }
                                    disabled={
                                      openingId ===
                                      `${catalog.id}-price-list-download-url`
                                    }
                                    sx={{
                                      color:
                                        catalogTokens.color
                                          .primary,
                                      border: `1px solid ${catalogTokens.color.border}`,
                                      borderRadius: `${catalogTokens.radius.md}px`,
                                    }}
                                  >
                                    <ReceiptLongOutlinedIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}

                            {/* Management Menu */}
                            {canManage && (
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  setMenuAnchor(
                                    e.currentTarget
                                  );
                                  setMenuCatalog(catalog);
                                }}
                                sx={{
                                  color:
                                    catalogTokens.color
                                      .textSecondary,
                                }}
                              >
                                <MoreVertIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Box>

                          {/* Remarks */}
                          {catalog.remarks && (
                            <Typography
                              variant="caption"
                              sx={{
                                display: "block",
                                mt: 1,
                                textAlign: "left",
                                color:
                                  catalogTokens.color
                                    .textSecondary,
                                whiteSpace: "normal",
                                overflowWrap: "anywhere",
                                lineHeight: 1.45,
                              }}
                            >
                              {catalog.remarks}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* =========================================================
          MANAGEMENT MENU
          ========================================================= */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
      >
        <MenuItem
          onClick={() => {
            const c = menuCatalog;
            closeMenu();
            setEditTarget(c);
          }}
        >
          <ListItemIcon>
            <EditOutlinedIcon fontSize="small" />
          </ListItemIcon>

          <ListItemText>Edit tags</ListItemText>
        </MenuItem>

        <MenuItem
          onClick={() => {
            const c = menuCatalog;
            closeMenu();
            setReplaceTarget(c);
          }}
        >
          <ListItemIcon>
            <PublishOutlinedIcon fontSize="small" />
          </ListItemIcon>

          <ListItemText>Replace catalog</ListItemText>
        </MenuItem>

        <MenuItem
          onClick={() => {
            const c = menuCatalog;
            closeMenu();
            setReplacePriceListTarget(c);
          }}
        >
          <ListItemIcon>
            <ReceiptLongOutlinedIcon fontSize="small" />
          </ListItemIcon>

          <ListItemText>Replace price list</ListItemText>
        </MenuItem>

        <MenuItem
          onClick={() => {
            const c = menuCatalog;
            closeMenu();
            handleRegenerateSummary(c);
          }}
          disabled={
            Boolean(
              menuCatalog &&
                menuCatalog.catalogSummary &&
                menuCatalog.catalogSummary.trim()
            ) ||
            regeneratingId === menuCatalog?.id
          }
        >
          <ListItemIcon>
            {regeneratingId === menuCatalog?.id ? (
              <CircularProgress size={16} />
            ) : (
              <AutoFixHighIcon fontSize="small" />
            )}
          </ListItemIcon>

          <ListItemText>
            Regenerate AI summary
          </ListItemText>
        </MenuItem>

        <MenuItem
          onClick={() => {
            const c = menuCatalog;
            closeMenu();
            setDeleteTarget(c);
          }}
        >
          <ListItemIcon>
            <DeleteOutlineIcon
              fontSize="small"
              sx={{ color: "#dc2626" }}
            />
          </ListItemIcon>

          <ListItemText sx={{ color: "#dc2626" }}>
            Delete
          </ListItemText>
        </MenuItem>
      </Menu>

      {/* =========================================================
          EDIT CATALOG
          ========================================================= */}
      <VendorCatalogEditDialog
        catalog={editTarget}
        open={!!editTarget}
        onClose={() => setEditTarget(null)}
        onSuccess={handleEditSuccess}
      />

      {/* =========================================================
          REPLACE CATALOG
          ========================================================= */}
      <VendorCatalogReplaceDialog
        catalog={replaceTarget}
        open={!!replaceTarget}
        onClose={() => setReplaceTarget(null)}
        onSuccess={handleReplaceSuccess}
      />

      {/* =========================================================
          REPLACE PRICE LIST
          ========================================================= */}
      <VendorCatalogReplacePriceListDialog
        catalog={replacePriceListTarget}
        open={!!replacePriceListTarget}
        onClose={() => setReplacePriceListTarget(null)}
        onSuccess={handleReplacePriceListSuccess}
      />

      {/* =========================================================
          DELETE CONFIRMATION
          ========================================================= */}
      <Dialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
      >
        <DialogTitle>Delete this catalog?</DialogTitle>

        <DialogContent>
          <DialogContentText>
            {deleteTarget && (
              <>
                This catalog (
                {formatPriceRange(
                  deleteTarget.minPrice,
                  deleteTarget.maxPrice
                )}
                ) will be permanently removed. This can't be
                undone.
              </>
            )}
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setDeleteTarget(null)}
            sx={{
              textTransform: "none",
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={confirmDelete}
            disabled={deletingId != null}
            color="error"
            variant="contained"
            disableElevation
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: `${catalogTokens.radius.md}px`,
            }}
          >
            {deletingId != null ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* =========================================================
          SNACKBAR
          ========================================================= */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() =>
          setSnackbar((s) => ({
            ...s,
            open: false,
          }))
        }
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() =>
            setSnackbar((s) => ({
              ...s,
              open: false,
            }))
          }
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default VendorCatalogVendorDialog;